import { MAP_FOLLOW } from '../config/timing.js';
/**
 * Wohin die Karte schaut — Vergrößerung und Zielpunkt.
 *
 * Framework-unabhängig und ohne Karte prüfbar: Was hier steht, ist eine
 * Rechnung, keine Darstellung. Die Karte setzt das Ergebnis nur um.
 *
 * Die Datei hieß einmal nach der Navigation, weil sie dort anfing. Sie gilt
 * längst für mehr: `navigationZoom` bestimmt die Vergrößerung auch in der
 * Ansicht „mein Fahrzeug", und `centerOffsetY` gilt für jede Ansicht, die
 * einen Punkt in die Mitte holt.
 *
 * ## Warum der Zielpunkt nicht die Bildmitte ist
 *
 * Die Karte füllt den ganzen Bildschirm (`.karte { position: absolute; inset: 0 }`),
 * und die Bedienleiste liegt **darüber**. Die Mitte der Karte ist damit nicht
 * die Mitte dessen, was man sieht: Auf 390 × 844 verdeckt die Leiste rund ein
 * Drittel, im Querformat mehr als die Hälfte. Wer in die geometrische Mitte
 * zielt, legt das eigene Fahrzeug unter die Schaltflächen.
 */
/**
 * Vergrößerung nach gefahrener Geschwindigkeit.
 *
 * Bei Schrittgeschwindigkeit will der Fahrer die nächste Einmündung sehen, auf
 * der Landstraße den nächsten Kilometer. Eine feste Stufe kann nur eines von
 * beidem — deshalb wird zwischen zwei Stützstellen gemischt.
 *
 * Das Ergebnis ist auf `navigationZoomStep` gerundet — und es bleibt stehen,
 * solange sich die Rechnung nicht um eine volle Stufe vom bisherigen Wert
 * entfernt hat. Beides zusammen, denn die Stufung allein genügt nicht: Sie
 * verschiebt das Flattern nur an die Stufengrenzen. Bei 60 km/h ergibt die
 * Rechnung 15,5 und bei 62 km/h 15,25 — ein GPS-Tempo schwankt um mehr als
 * das, und die Karte atmete im Sekundentakt.
 *
 * `previous` ist deshalb kein Beiwerk: Ohne den bisherigen Wert gibt es keine
 * Rückschau und keine Ruhe.
 *
 * Ohne gemeldete Geschwindigkeit gilt die nähere Stufe: Wer nicht weiß, wie
 * schnell er ist, sieht lieber zu viel Umgebung als zu wenig.
 */
export function navigationZoom(speedMps, previous = null) {
    const { navigationZoomSlowKmh, navigationZoomFastKmh, navigationZoomClose, navigationZoomFar, navigationZoomStep, } = MAP_FOLLOW;
    if (speedMps === null || !Number.isFinite(speedMps) || speedMps < 0) {
        return previous ?? navigationZoomClose;
    }
    const kmh = speedMps * 3.6;
    const anteil = Math.min(1, Math.max(0, (kmh - navigationZoomSlowKmh) / (navigationZoomFastKmh - navigationZoomSlowKmh)));
    const roh = navigationZoomClose + anteil * (navigationZoomFar - navigationZoomClose);
    // Die Rückschau: Erst eine volle Stufe Abstand setzt die Karte in Bewegung.
    if (previous !== null && Math.abs(roh - previous) < navigationZoomStep) {
        return previous;
    }
    return Math.round(roh / navigationZoomStep) * navigationZoomStep;
}
/**
 * Wie weit das eigene Fahrzeug unter der Bildmitte steht, in Bildpunkten.
 *
 * **Der Name ist enger als der Gebrauch.** Seit dem 04.09.2026 gilt diese
 * Rechnung für jede Ansicht, in der die Karte **in Fahrtrichtung** steht — auch
 * ohne Navigation (A-105). Sie heißt trotzdem weiter so: A-80 und A-82 sind
 * unter diesem Namen entschieden, und ein umbenannter Bezeichner macht das
 * Entscheidungsbuch an zwei Stellen falsch.
 *
 * Nicht als feste Zahl, sondern als Anteil der Kartenhöhe: Ein Telefon im
 * Hochformat und eine Tablette quer haben sehr verschiedene Höhen, und in
 * beiden soll ungefähr gleich viel Strecke nach vorn sichtbar bleiben.
 *
 * Der Wert ist die Verschiebung des Zielpunktes gegenüber der Mitte. Positiv
 * heißt nach unten — das Fahrzeug rutscht hinunter, der Platz darüber gehört
 * dem Weg.
 */
export function navigationOffsetY(mapHeightPx, verdecktPx = 0) {
    if (!Number.isFinite(mapHeightPx) || mapHeightPx <= 0) {
        return 0;
    }
    const verdeckt = Number.isFinite(verdecktPx) ? Math.max(0, Math.min(verdecktPx, mapHeightPx)) : 0;
    const sichtbar = mapHeightPx - verdeckt;
    /*
     * Ist unten alles zu, gibt es nichts mehr auszurichten — dann bleibt es bei
     * der Mitte, statt das Fahrzeug an den oberen Rand zu drücken.
     */
    if (sichtbar <= 0) {
        return 0;
    }
    /*
     * So tief wie möglich — aber nie unter die Mitte des sichtbaren Streifens.
     *
     * Der zweite Teil greift nur, wenn unten fast alles zu ist: Dann läge das
     * Fahrzeug sonst auf der Kante, und ein Stück Straße hinter ihm wäre nicht
     * mehr zu sehen.
     */
    const ziel = Math.max(sichtbar - MAP_FOLLOW.navigationBottomMarginPx, sichtbar / 2);
    return ziel - mapHeightPx / 2;
}
/**
 * Wie weit der Zielpunkt aus der Bildmitte rücken muss, damit er in der Mitte
 * des **sichtbaren** Streifens landet.
 *
 * Der Wert ist negativ: Ist unten etwas verdeckt, wandert das Ziel nach oben,
 * und zwar um die halbe verdeckte Höhe. Ist nichts verdeckt, ist es null und
 * die Karte zielt wie zuvor in die geometrische Mitte.
 *
 * Am 04.09.2026 fotografiert: Auf 844 × 390 lag das eigene Fahrzeug halb unter
 * „Führungsfahrzeug zeigen", auf 390 × 844 rund 120 Bildpunkte zu tief. Die
 * Zusicherung der Oberflächenprüfung galt bis dahin für das Kartenfeld; das
 * Auge sieht aber nur dessen freien Teil (A-103).
 *
 * Gegenstück zu `navigationOffsetY`, das denselben Streifen kennt, aber ein
 * anderes Ziel darin hat: dort so tief wie möglich, damit der Weg nach vorn
 * Platz bekommt, hier mittig.
 */
export function centerOffsetY(mapHeightPx, verdecktPx = 0) {
    if (!Number.isFinite(mapHeightPx) || mapHeightPx <= 0) {
        return 0;
    }
    const verdeckt = Number.isFinite(verdecktPx) ? Math.max(0, Math.min(verdecktPx, mapHeightPx)) : 0;
    /*
     * Ist unten alles zu, gibt es nichts mehr auszurichten — dann bleibt es bei
     * der Mitte, statt den Punkt an den oberen Rand zu drücken. Dieselbe
     * Überlegung wie in `navigationOffsetY`.
     */
    if (mapHeightPx - verdeckt <= 0) {
        return 0;
    }
    // Ohne verdeckte Fläche eine glatte Null: `-0 / 2` ist `-0`, und das ist für
    // `Object.is` etwas anderes als null.
    return verdeckt === 0 ? 0 : -verdeckt / 2;
}
/**
 * Der Zielpunkt für „mein Fahrzeug" — er hängt an der **Ausrichtung**.
 *
 * ## Warum nicht immer dasselbe
 *
 * Steht die Karte **nach Norden**, ist „oben" nicht „vorn". Ein Versatz nach
 * unten verschenkt dann nur Bild in eine beliebige Richtung; mittig ist richtig.
 *
 * Steht sie **in Fahrtrichtung**, ist oben genau das, was kommt. Dann gehört
 * dem Weg voraus der Platz, und das Fahrzeug rückt ins untere Drittel — dieselbe
 * Rechnung, die die Navigationsansicht seit A-82 benutzt: so tief wie möglich,
 * mit `navigationBottomMarginPx` Luft über der Bedienung und nie über der Mitte
 * des sichtbaren Streifens.
 *
 * Markus am 04.09.2026, nachdem A-103 die Ansicht auf die Mitte des sichtbaren
 * Teils gestellt hatte: *„Wenn die Karte nach Norden ausgerichtet ist, dann ist
 * das so gut. Aber wenn die Karte in Fahrtrichtung ausgerichtet ist, dann wäre
 * es meiner Meinung nach besser, wenn man mehr von der Karte vor sich hat. Das
 * Fahrzeug im unteren Drittel ist dann schon ganz richtig!"*
 *
 * Kein neuer Schwellwert: Beide Fälle greifen auf Rechnungen zurück, die es
 * schon gibt.
 */
export function followOffsetY(mapHeightPx, verdecktPx, inFahrtrichtung) {
    return inFahrtrichtung
        ? navigationOffsetY(mapHeightPx, verdecktPx)
        : centerOffsetY(mapHeightPx, verdecktPx);
}
//# sourceMappingURL=navigationView.js.map